import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { formatDateTime } from "@/lib/format";

interface SystemConfigItem {
  config_key: string;
  config_value: string;
  description: string | null;
  is_system: boolean;
  updated_at: string | null;
}

const ENDPOINT = "/quantum/business/system-configs";
const KEY_REGEX = /^[a-z][a-z0-9_]*$/;

function tryFormat(value: string): { ok: boolean; output: string } {
  try {
    return { ok: true, output: JSON.stringify(JSON.parse(value), null, 2) };
  } catch {
    return { ok: false, output: value };
  }
}

function isValidJson(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

function extractApiError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: string; message?: string } | undefined)
      ?.detail;
    const message = (err.response?.data as { message?: string } | undefined)?.message;
    return detail || message || err.message || fallback;
  }
  return fallback;
}

function truncate(text: string, length = 80): string {
  if (text.length <= length) return text;
  return `${text.slice(0, length)}…`;
}

export default function SystemConfigPage() {
  const { hasRole } = useAdminAuth();
  const isSuperAdmin = hasRole(["super_admin"]);

  const [items, setItems] = useState<SystemConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ value: string; description: string }>({
    value: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<{
    config_key: string;
    description: string;
    config_value: string;
  }>({ config_key: "", description: "", config_value: "{}" });
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(ENDPOINT, { params: { page: 1, size: 100 } });
      const payload = response.data?.data ?? {};
      const list: SystemConfigItem[] = Array.isArray(payload.items) ? payload.items : [];
      setItems(list);
    } catch (err) {
      console.error("Fetch system configs error:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const filteredItems = useMemo(() => {
    const kw = search.trim().toLowerCase();
    if (!kw) return items;
    return items.filter((item) => {
      const haystack = `${item.config_key} ${item.description ?? ""} ${item.config_value}`.toLowerCase();
      return haystack.includes(kw);
    });
  }, [items, search]);

  const toggleExpand = (item: SystemConfigItem) => {
    if (expandedKey === item.config_key) {
      setExpandedKey(null);
      setError(null);
      return;
    }
    setExpandedKey(item.config_key);
    setDraft({ value: item.config_value, description: item.description ?? "" });
    setError(null);
  };

  const formatDraft = () => {
    const { ok, output } = tryFormat(draft.value);
    if (!ok) {
      setError("当前 value 不是合法 JSON，无法格式化");
      return;
    }
    setDraft((prev) => ({ ...prev, value: output }));
    setError(null);
  };

  const saveDraft = async (item: SystemConfigItem) => {
    if (!isValidJson(draft.value)) {
      setError("config_value 必须是合法 JSON");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.put(`${ENDPOINT}/${encodeURIComponent(item.config_key)}`, {
        config_value: draft.value,
        description: draft.description,
      });
      await fetchData();
      setExpandedKey(null);
    } catch (err) {
      setError(extractApiError(err, "保存失败，请稍后重试"));
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async (item: SystemConfigItem) => {
    if (item.is_system) return;
    if (!window.confirm(`确认删除配置 "${item.config_key}"？此操作不可恢复。`)) return;
    try {
      await api.delete(`${ENDPOINT}/${encodeURIComponent(item.config_key)}`);
      await fetchData();
    } catch (err) {
      window.alert(extractApiError(err, "删除失败"));
    }
  };

  const createConfig = async () => {
    setCreateError(null);
    const key = createForm.config_key.trim();
    if (!KEY_REGEX.test(key)) {
      setCreateError("config_key 必须以小写字母开头，仅可包含小写字母、数字与下划线");
      return;
    }
    if (!isValidJson(createForm.config_value)) {
      setCreateError("config_value 必须是合法 JSON");
      return;
    }
    setCreating(true);
    try {
      await api.post(ENDPOINT, {
        config_key: key,
        config_value: createForm.config_value,
        description: createForm.description || null,
      });
      setShowCreate(false);
      setCreateForm({ config_key: "", description: "", config_value: "{}" });
      await fetchData();
    } catch (err) {
      setCreateError(extractApiError(err, "新建失败，请检查 key 是否已存在"));
    } finally {
      setCreating(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="bg-white rounded-xl border border-surface-border p-8 text-center">
        <i className="ri-lock-line text-text-muted text-3xl" />
        <h3 className="mt-2 text-text-primary text-base font-medium">无权限访问</h3>
        <p className="mt-1 text-text-muted text-sm">系统配置(JSON) 仅超级管理员可用</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">系统配置（JSON）</h2>
          <p className="text-text-muted text-xs mt-1">
            共 {items.length} 项配置 · value 以 JSON 格式存储
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 rounded-lg bg-brand text-white text-sm font-medium hover:bg-brand-dark transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line mr-1" />
          新建配置
        </button>
      </div>

      <div className="bg-white rounded-xl border border-surface-border p-4">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索 key / description / value …"
            className="w-full h-10 pl-9 pr-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-surface-border overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 bg-surface/50 border-b border-surface-border text-xs text-text-secondary font-medium">
          <div className="col-span-4">配置 Key</div>
          <div className="col-span-4">Value 预览</div>
          <div className="col-span-3">更新时间</div>
          <div className="col-span-1 text-right">操作</div>
        </div>

        {loading ? (
          <div className="px-5 py-8 text-center text-text-muted text-sm">加载中…</div>
        ) : filteredItems.length === 0 ? (
          <div className="px-5 py-12 text-center text-text-muted text-sm">
            {search ? "没有匹配的配置项" : "暂无配置项"}
          </div>
        ) : (
          filteredItems.map((item) => {
            const isOpen = expandedKey === item.config_key;
            return (
              <div key={item.config_key} className="border-b border-surface-border last:border-b-0">
                <div
                  className="grid grid-cols-12 items-center px-5 py-3 hover:bg-surface/40 cursor-pointer"
                  onClick={() => toggleExpand(item)}
                >
                  <div className="col-span-4 min-w-0">
                    <div className="flex items-center gap-2">
                      <i
                        className={`ri-arrow-right-s-line text-text-muted transition-transform ${
                          isOpen ? "rotate-90" : ""
                        }`}
                      />
                      <span className="font-mono text-sm text-text-primary truncate">
                        {item.config_key}
                      </span>
                      {item.is_system && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-status-danger/10 text-status-danger font-medium">
                          内置
                        </span>
                      )}
                    </div>
                    {item.description && (
                      <p className="ml-6 text-text-muted text-xs mt-0.5 truncate">{item.description}</p>
                    )}
                  </div>
                  <div className="col-span-4 text-sm text-text-secondary font-mono truncate">
                    {truncate(item.config_value, 60)}
                  </div>
                  <div className="col-span-3 text-text-muted text-xs">
                    {item.updated_at ? formatDateTime(item.updated_at) : "—"}
                  </div>
                  <div className="col-span-1 flex justify-end">
                    {item.is_system ? (
                      <button
                        disabled
                        title="系统内置配置不可删除"
                        className="w-8 h-8 rounded-md text-text-muted/40 cursor-not-allowed"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void deleteConfig(item);
                        }}
                        className="w-8 h-8 rounded-md text-text-muted hover:bg-status-danger/10 hover:text-status-danger transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="px-5 py-4 bg-surface/40 border-t border-surface-border space-y-3">
                    {item.is_system && (
                      <div className="px-3 py-2 rounded-lg bg-status-danger/10 border border-status-danger/30 text-status-danger text-xs">
                        <i className="ri-error-warning-line mr-1" />
                        系统内置配置，修改将影响多模块运行，删除已禁用
                      </div>
                    )}
                    <div>
                      <label className="text-xs text-text-secondary">描述</label>
                      <input
                        type="text"
                        value={draft.description}
                        onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
                        className="mt-1 w-full h-9 px-3 rounded-lg bg-white text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs text-text-secondary">Value（JSON）</label>
                        <button
                          onClick={formatDraft}
                          className="text-xs text-brand hover:underline cursor-pointer"
                        >
                          <i className="ri-magic-line mr-0.5" />
                          格式化
                        </button>
                      </div>
                      <textarea
                        value={draft.value}
                        onChange={(e) => setDraft((p) => ({ ...p, value: e.target.value }))}
                        rows={Math.min(20, Math.max(6, draft.value.split("\n").length))}
                        className={`w-full px-3 py-2 rounded-lg bg-white text-sm font-mono border focus:outline-none ${
                          isValidJson(draft.value)
                            ? "border-surface-border focus:border-brand/50"
                            : "border-status-danger/40 focus:border-status-danger"
                        }`}
                      />
                      {!isValidJson(draft.value) && (
                        <p className="text-status-danger text-xs mt-1">
                          <i className="ri-error-warning-line mr-1" />
                          当前内容不是合法 JSON
                        </p>
                      )}
                    </div>
                    {error && (
                      <div className="text-status-danger text-xs">
                        <i className="ri-error-warning-line mr-1" />
                        {error}
                      </div>
                    )}
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setExpandedKey(null);
                          setError(null);
                        }}
                        className="px-4 py-2 rounded-lg border border-surface-border text-sm text-text-secondary hover:bg-surface transition-colors cursor-pointer"
                      >
                        取消
                      </button>
                      <button
                        onClick={() => void saveDraft(item)}
                        disabled={saving || !isValidJson(draft.value)}
                        className="px-4 py-2 rounded-lg bg-brand text-white text-sm font-medium hover:bg-brand-dark transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving ? "保存中…" : "保存"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-[520px] rounded-2xl border border-surface-border bg-white shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border">
              <h3 className="text-base font-semibold text-text-primary">新建配置</h3>
              <button
                onClick={() => {
                  setShowCreate(false);
                  setCreateError(null);
                }}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:bg-surface"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="text-xs text-text-secondary">config_key</label>
                <input
                  type="text"
                  value={createForm.config_key}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, config_key: e.target.value }))
                  }
                  placeholder="例如：agent_max_leverage"
                  className="mt-1 w-full h-10 px-3 rounded-lg bg-surface text-sm font-mono text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
                />
                <p className="text-text-muted text-[11px] mt-1">
                  小写字母开头，仅可包含小写字母、数字、下划线
                </p>
              </div>
              <div>
                <label className="text-xs text-text-secondary">描述</label>
                <input
                  type="text"
                  value={createForm.description}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, description: e.target.value }))
                  }
                  className="mt-1 w-full h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-text-secondary">Value（JSON）</label>
                <textarea
                  value={createForm.config_value}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, config_value: e.target.value }))
                  }
                  rows={6}
                  className={`mt-1 w-full px-3 py-2 rounded-lg bg-surface text-sm font-mono border focus:outline-none ${
                    isValidJson(createForm.config_value)
                      ? "border-surface-border focus:border-brand/50"
                      : "border-status-danger/40 focus:border-status-danger"
                  }`}
                />
              </div>
              {createError && (
                <div className="text-status-danger text-xs">
                  <i className="ri-error-warning-line mr-1" />
                  {createError}
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-surface-border flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCreate(false);
                  setCreateError(null);
                }}
                className="px-4 py-2 rounded-lg border border-surface-border text-sm text-text-secondary hover:bg-surface transition-colors cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={() => void createConfig()}
                disabled={creating}
                className="px-4 py-2 rounded-lg bg-brand text-white text-sm font-medium hover:bg-brand-dark transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? "创建中…" : "创建"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
